import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import * as transfersActions from './billing.transfers.actions';
import * as transfersSelectors from './billing.transfers.selectors';
import { BillingService } from '../services/billing.service';
import { MessageTranslateService } from '../../../shared/services/message.service';

export class BillingTransfersEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private billingService = inject(BillingService);
  private messageService = inject(MessageTranslateService);

  initTransfers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.initTransfers),
      switchMap(() =>
        of(
          transfersActions.loadTransferFilterParameters(),
          transfersActions.loadTransfers(),
        ),
      ),
    ),
  );

  loadTransfers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        transfersActions.loadTransfers,
        transfersActions.changeTransferFilterSuccess,
        transfersActions.changeTransferPaginationSuccess,
        transfersActions.changeTransferDateRangeSuccess,
        transfersActions.recallTransferSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(transfersSelectors.selectTransferPagination),
        this.store.select(transfersSelectors.selectTransferCurrentFilter),
        this.store.select(transfersSelectors.selectTransferDateFrom),
        this.store.select(transfersSelectors.selectTransferDateTo),
      ]),
      switchMap(([_action, pagination, filter, dateFrom, dateTo]) =>
        this.billingService
          .searchTransfers({
            pageIndex: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 10,
            operatorIds: filter?.operatorIds ?? null,
            valueFrom: filter?.value?.[0] ?? null,
            valueTo: filter?.value?.[1] ?? null,
            dateFrom: filter?.dateFrom ?? dateFrom ?? null,
            dateTo: filter?.dateTo ?? dateTo ?? null,
          })
          .pipe(
            map((res) =>
              res.isSuccess
                ? transfersActions.loadTransfersSuccess({ response: res.value! })
                : transfersActions.loadTransfersFailure({ error: res.error! }),
            ),
            catchError(() =>
              of(
                transfersActions.loadTransfersFailure({
                  error: { errorMessage: 'Loading transfers failed' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadFilterParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.loadTransferFilterParameters),
      concatLatestFrom(() => [this.store.select(transfersSelectors.selectTransferOperatorId)]),
      switchMap(([_action, operatorId]) => {
        if (!operatorId) {
          return of(
            transfersActions.loadTransferFilterParametersFailure({
              error: { errorMessage: 'No operator ID' },
            }),
          );
        }
        return this.billingService.getTransferFilters(operatorId).pipe(
          map((res) =>
            res.isSuccess
              ? transfersActions.loadTransferFilterParametersSuccess({
                  filterParameters: res.value!,
                })
              : transfersActions.loadTransferFilterParametersFailure({ error: res.error! }),
          ),
          catchError(() =>
            of(
              transfersActions.loadTransferFilterParametersFailure({
                error: { errorMessage: 'Loading filter parameters failed' },
              }),
            ),
          ),
        );
      }),
    ),
  );

  changeFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.changeTransferFilter),
      map(() => transfersActions.changeTransferFilterSuccess()),
    ),
  );

  changePagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.changeTransferPagination),
      map(() => transfersActions.changeTransferPaginationSuccess()),
    ),
  );

  changeDateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.changeTransferDateRange),
      map(() => transfersActions.changeTransferDateRangeSuccess()),
    ),
  );

  recallTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transfersActions.recallTransfer),
      switchMap(({ transferId }) =>
        this.billingService.recallTransfer(transferId).pipe(
          map((res) =>
            res.isSuccess
              ? transfersActions.recallTransferSuccess()
              : transfersActions.recallTransferFailure({ error: res.error! }),
          ),
          catchError(() =>
            of(
              transfersActions.recallTransferFailure({
                error: { errorMessage: 'Recall transfer failed' },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  recallTransferSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(transfersActions.recallTransferSuccess),
        tap(() => this.messageService.successTranslate('billing.payouts.transfers.recall.success')),
      ),
    { dispatch: false },
  );

  recallTransferFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(transfersActions.recallTransferFailure),
        tap(() => this.messageService.errorTranslate('billing.payouts.transfers.recall.error')),
      ),
    { dispatch: false },
  );
}
